import { axios, transformAxiosError } from "../utils/axios.create";
import { AccountSetting } from "../domain/settings.interface";

export type FindAllAccountSettingsPayload = {
  settingId?: string;
  active?: boolean | "";
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

export type AccountSettingPayload = {
  settingId: string;
  value: unknown;
  active: boolean;
  createdBy?: string;
  updatedBy?: string;
};

export type AccountSettingsListResponse = {
  list: AccountSetting[];
  totalCount: number;
};

const findAll = async ({
  settingId,
  active,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: FindAllAccountSettingsPayload): Promise<AccountSettingsListResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  if (settingId) {
    queryParams.append("settingId", settingId);
  }

  if (active !== "" && active !== undefined) {
    queryParams.append("active", String(active));
  }

  return axios
    .get(`/account-settings?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: body?.list || [],
        totalCount: body?.totalCount || 0,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOne = async (id: string): Promise<AccountSetting> => {
  return axios
    .get(`/account-settings/find/${id}`)
    .then((res) => {
      return res.data.body as AccountSetting;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: AccountSettingPayload): Promise<AccountSetting> => {
  return axios
    .post("/account-settings", data)
    .then((res) => {
      return res.data.body as AccountSetting;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: AccountSettingPayload;
}): Promise<AccountSetting> => {
  return axios
    .put(`/account-settings/${id}`, data)
    .then((res) => {
      return res.data.body as AccountSetting;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/account-settings/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const AccountSettingsService = {
  findAll,
  findOne,
  create,
  update,
  archive,
};
